import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '@core/api/baseQuery';
import type { HisService, HisServiceScope, PaginatedResponse, ServiceFacets } from '@core/types/api';

export interface ListServicesArgs {
  page: number;
  pageSize: number;
  ordering?: string;
  search?: string;
  scope?: HisServiceScope;
  /** Any of these billing areas (repeated in the query string). */
  billingAreas?: string[];
  /** Any of these groups (repeated in the query string). */
  groups?: string[];
}

export interface ServicePage {
  results: HisService[];
  count: number;
}

/**
 * What the services table asks the server for (see `linkParams` in tariffs).
 *
 * A query string rather than a params object: a filter with several values is the
 * same key repeated (`billing_area=A&billing_area=B`), which `URLSearchParams` can
 * say and a plain object cannot.
 */
export function serviceParams({
  page,
  pageSize,
  ordering,
  search,
  scope,
  billingAreas,
  groups,
}: ListServicesArgs): URLSearchParams {
  const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
  if (ordering) params.set('ordering', ordering);
  if (search) params.set('search', search);
  if (scope) params.set('scope', scope);
  for (const area of billingAreas ?? []) params.append('billing_area', area);
  for (const group of groups ?? []) params.append('group', group);
  return params;
}

/**
 * Portfolio API: the centre's own services on the active company, and which
 * portfolio (cartera) each belongs to.
 *
 * COMPANY-scoped, so the cache is dropped when the organization or the company
 * changes (`TENANT_SCOPED_APIS` and `COMPANY_SCOPED_APIS`). A scope change also
 * touches the tariff links, which the store invalidates without a cross-feature
 * import (see `assignScope.matchFulfilled` there).
 */
export const portfolioApi = createApi({
  reducerPath: 'portfolioApi',
  baseQuery,
  tagTypes: ['Service', 'Facets'],
  endpoints: (builder) => ({
    listServices: builder.query<ServicePage, ListServicesArgs>({
      query: (args) => ({ url: `/api/tariffs/services/?${serviceParams(args)}`, method: 'GET' }),
      transformResponse: (response: PaginatedResponse<HisService>) => ({
        results: response?.results ?? [],
        count: response?.count ?? 0,
      }),
      providesTags: ['Service'],
    }),

    getFacets: builder.query<ServiceFacets, void>({
      query: () => ({ url: '/api/tariffs/services/facets/', method: 'GET' }),
      providesTags: ['Facets'],
    }),

    assignScope: builder.mutation<{ updated: number }, { uuids: string[]; scope: HisServiceScope }>({
      query: (body) => ({ url: '/api/tariffs/services/assign-scope/', method: 'POST', body }),
      invalidatesTags: ['Service', 'Facets'],
    }),
  }),
});

export const { useListServicesQuery, useGetFacetsQuery, useAssignScopeMutation } = portfolioApi;
